import { Controller } from "@hotwired/stimulus"
import { loadPlan, savePlan } from "plan_storage"
import { renderIcons } from "lucide_icons"
import { saveGoalToLibrary } from "goal_library"
import { vendorGet } from "vendor_library"
import {
  COPY,
  candidatesFromServices,
  candidatesFromNeeds,
  composeGoalText,
  walkthroughSheetHtml,
} from "goal_walkthrough_view"

// Goal Walkthrough Controller — guides users through the SMART goal process.
// Steps:
// 1. Review existing services (Continue/Change/Discontinue)
// 2. Select unmet needs as goal candidates
// 3. Shape each goal with SMART prompts
// 4. Preview and save
//
// The walkthrough reads/writes through plan_store, keeping it independent
// of the wizard's internal state.

export default class extends Controller {
  static values = {
    existingGoalId: String, // When refining an existing goal
    skipPreference: { type: Boolean, default: false },
  }

  connect() {
    // Controller manages state internally; sheet is created on open()
  }

  disconnect() {
    this._close()
  }

  // Entry point: open the walkthrough sheet
  open(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    // Check skip preference
    if (this.skipPreferenceValue && !this.existingGoalIdValue) {
      // Skip walkthrough - let the default add behavior proceed
      return
    }

    this._initState()
    this._render()
  }

  // Entry point from a goal row's "Refine" button
  openForRow(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    const row = event.currentTarget.closest("[data-row]")
    if (!row) return
    const goalId = row.dataset.id
    if (goalId) {
      this.openForGoal(goalId)
    }
  }

  // Open with an existing goal for refinement
  openForGoal(goalId) {
    this.existingGoalIdValue = goalId
    this._initState()

    // Pre-fill from existing goal's SMART data
    const plan = loadPlan()
    const goal = (plan.goals || []).find(g => g.id === goalId)
    if (goal && goal.smart) {
      this._smart = { ...goal.smart }
    }
    if (goal) {
      // Create a single candidate from the existing goal
      this._candidates = [{
        id: goal.id,
        type: "existing",
        text: goal.heading || goal.description || "",
        sourceServiceId: goal.sourceServiceId,
        sourceNeedId: goal.sourceNeedId,
      }]
      // Jump to step 3 for existing goals
      this._step = 3
    }

    this._render()
  }

  _initState() {
    const plan = loadPlan()

    this._step = 1
    this._plan = plan

    // Service decisions: { serviceId: { decision, futureSupport, futureSupportWhy } }
    this._serviceDecisions = {}

    // Selected need IDs
    this._selectedNeedIds = new Set()

    // Goal candidates derived from services + needs
    this._candidates = []
    this._candidateIndex = 0

    // SMART values for current candidate
    this._smart = { s: "", m: "", a: "", r: "", t: "" }
    this._vendorId = null

    // Save to library checkbox
    this._saveToLibrary = false

    // Load current services and unmet needs from plan
    this._services = this._loadServices(plan)
    this._needs = this._loadNeeds(plan)
  }

  _loadServices(plan) {
    const cs = plan.currentServices || []
    return cs.map(s => ({
      id: s.id || `svc-${Date.now()}-${Math.random()}`,
      text: s.text || s.name || "",
      vendorId: s.vendorId,
      vendorName: s.vendorId ? (vendorGet(s.vendorId) || {}).name : "",
    }))
  }

  _loadNeeds(plan) {
    const un = plan.unmetNeeds || []
    return un.map(n => ({
      id: n.id || `need-${Date.now()}-${Math.random()}`,
      text: n.text || n.description || "",
      vendorId: n.vendorId,
      vendorName: n.vendorId ? (vendorGet(n.vendorId) || {}).name : "",
    }))
  }

  _render() {
    // Build candidates from decisions
    if (this._step >= 3 && this._candidates.length === 0) {
      this._buildCandidates()
    }

    const html = walkthroughSheetHtml({
      step: this._step,
      services: this._services,
      serviceDecisions: this._serviceDecisions,
      needs: this._needs,
      selectedNeedIds: this._selectedNeedIds,
      candidates: this._candidates,
      candidateIndex: this._candidateIndex,
      smart: this._smart,
      saveToLibrary: this._saveToLibrary,
    })

    // Remove existing overlay if any
    this._close()

    // Create and append new overlay
    this._overlay = document.createElement("div")
    this._overlay.setAttribute("role", "dialog")
    this._overlay.setAttribute("aria-modal", "true")
    this._overlay.innerHTML = html
    document.body.appendChild(this._overlay)

    // Render Lucide icons
    renderIcons(this._overlay)

    // Bind events
    this._bindEvents()

    // Focus first interactive element
    this._focusFirst()
  }

  _bindEvents() {
    if (!this._overlay) return

    // Navigation buttons
    this._overlay.querySelectorAll("[data-action]").forEach(btn => {
      btn.addEventListener("click", (e) => this._handleAction(e))
    })

    // Service decision buttons
    this._overlay.querySelectorAll(".goal-walkthrough__decision-btn").forEach(btn => {
      btn.addEventListener("click", (e) => this._handleServiceDecision(e))
    })

    // Future support buttons
    this._overlay.querySelectorAll(".goal-walkthrough__future-btn").forEach(btn => {
      btn.addEventListener("click", (e) => this._handleFutureSupport(e))
    })

    // Future support why input
    this._overlay.querySelectorAll(".goal-walkthrough__future-why").forEach(input => {
      input.addEventListener("input", (e) => this._handleFutureSupportWhy(e))
    })

    // Need checkboxes
    this._overlay.querySelectorAll(".goal-walkthrough__need-checkbox").forEach(cb => {
      cb.addEventListener("change", (e) => this._handleNeedToggle(e))
    })

    // SMART field inputs
    this._overlay.querySelectorAll(".goal-walkthrough__smart-input").forEach(input => {
      input.addEventListener("input", (e) => this._handleSmartInput(e))
    })

    // Library checkbox
    const libCheck = this._overlay.querySelector("[data-save-to-library]")
    if (libCheck) {
      libCheck.addEventListener("change", (e) => {
        this._saveToLibrary = e.target.checked
      })
    }

    // Escape key to close
    this._escHandler = (e) => {
      if (e.key === "Escape") {
        e.preventDefault()
        this._close()
      }
    }
    document.addEventListener("keydown", this._escHandler)
  }

  _handleAction(event) {
    const action = event.currentTarget.dataset.action
    switch (action) {
      case "cancel":
        this._close()
        break
      case "back":
        this._goBack()
        break
      case "next":
        this._goNext()
        break
      case "skip":
        this._goNext()
        break
      case "save":
        this._saveGoal()
        break
      case "save-next":
        this._saveGoal(true)
        break
    }
  }

  _handleServiceDecision(event) {
    const btn = event.currentTarget
    const row = btn.closest(".goal-walkthrough__service-row")
    if (!row) return

    const serviceId = row.dataset.serviceId
    const decision = btn.dataset.decision

    if (!this._serviceDecisions[serviceId]) {
      this._serviceDecisions[serviceId] = {}
    }
    this._serviceDecisions[serviceId].decision = decision

    // Update UI
    row.querySelectorAll(".goal-walkthrough__decision-btn").forEach(b => {
      b.classList.toggle("is-selected", b === btn)
    })

    // Show/hide future support based on decision
    const futureDiv = row.querySelector(".goal-walkthrough__future-support")
    if (futureDiv) {
      futureDiv.hidden = decision === "discontinue"
    }
  }

  _handleFutureSupport(event) {
    const btn = event.currentTarget
    const row = btn.closest(".goal-walkthrough__service-row")
    if (!row) return

    const serviceId = row.dataset.serviceId
    const futureValue = btn.dataset.future

    if (!this._serviceDecisions[serviceId]) {
      this._serviceDecisions[serviceId] = {}
    }
    this._serviceDecisions[serviceId].futureSupport = futureValue

    // Update UI
    row.querySelectorAll(".goal-walkthrough__future-btn").forEach(b => {
      b.classList.toggle("is-selected", b === btn)
    })
  }

  _handleFutureSupportWhy(event) {
    const input = event.currentTarget
    const row = input.closest(".goal-walkthrough__service-row")
    if (!row) return

    const serviceId = row.dataset.serviceId
    if (!this._serviceDecisions[serviceId]) {
      this._serviceDecisions[serviceId] = {}
    }
    this._serviceDecisions[serviceId].futureSupportWhy = input.value
  }

  _handleNeedToggle(event) {
    const cb = event.currentTarget
    const row = cb.closest(".goal-walkthrough__need-row")
    if (!row) return

    const needId = row.dataset.needId
    if (cb.checked) {
      this._selectedNeedIds.add(needId)
    } else {
      this._selectedNeedIds.delete(needId)
    }
  }

  _handleSmartInput(event) {
    const input = event.currentTarget
    const field = input.dataset.field
    if (field && this._smart) {
      this._smart[field] = input.value
    }
  }

  _buildCandidates() {
    // Build service candidates from decisions
    const serviceData = this._services.map(s => ({
      ...s,
      decision: (this._serviceDecisions[s.id] || {}).decision,
      futureSupport: (this._serviceDecisions[s.id] || {}).futureSupport,
      futureSupportWhy: (this._serviceDecisions[s.id] || {}).futureSupportWhy,
    }))
    const serviceCandidates = candidatesFromServices(serviceData)

    // Build need candidates from selections
    const needData = this._needs
      .filter(n => this._selectedNeedIds.has(n.id))
      .map(n => ({ ...n, isCandidate: true }))
    const needCandidates = candidatesFromNeeds(needData)

    this._candidates = [...serviceCandidates, ...needCandidates]
  }

  _goBack() {
    if (this._step > 1) {
      this._step--
      this._render()
    }
  }

  _goNext() {
    if (this._step < 4) {
      // Build candidates when leaving step 2
      if (this._step === 2) {
        this._buildCandidates()
        this._candidateIndex = 0
        this._smart = { s: "", m: "", a: "", r: "", t: "" }

        // Pre-fill Specific from first candidate
        if (this._candidates.length > 0) {
          this._smart.s = this._candidates[0].text || ""
        }
      }
      this._step++
      this._render()
    }
  }

  _saveGoal(andContinue = false) {
    const candidate = this._candidates[this._candidateIndex]
    if (!candidate) return

    const plan = loadPlan()
    if (!Array.isArray(plan.goals)) plan.goals = []

    const goalId = this.existingGoalIdValue || `goal-${Date.now()}-${Math.random().toString(36).slice(2)}`

    const newGoal = {
      id: goalId,
      heading: (this._smart.s || candidate.text || "").trim(),
      description: composeGoalText(this._smart),
      timeline: (this._smart.t || "").trim(),
      smart: { ...this._smart },
      sourceServiceId: candidate.sourceServiceId || null,
      sourceNeedId: candidate.sourceNeedId || null,
    }

    // Add vendor if selected
    if (this._vendorId) {
      newGoal.resources = [{
        id: `res-${Date.now()}`,
        vendorId: this._vendorId,
      }]
    }

    // Update or add goal
    const existingIndex = plan.goals.findIndex(g => g.id === goalId)
    if (existingIndex >= 0) {
      plan.goals[existingIndex] = { ...plan.goals[existingIndex], ...newGoal }
    } else {
      plan.goals.push(newGoal)
    }

    savePlan({ goals: plan.goals })

    // Save to library if checked
    if (this._saveToLibrary) {
      saveGoalToLibrary({
        heading: newGoal.heading,
        description: newGoal.description,
        timeline: newGoal.timeline,
        domain: "",
        category: "other",
      }, null, () => {})
    }

    if (andContinue && this._candidateIndex < this._candidates.length - 1) {
      // Move to next candidate
      this._candidateIndex++
      this._smart = { s: "", m: "", a: "", r: "", t: "" }
      // Pre-fill from next candidate
      const nextCandidate = this._candidates[this._candidateIndex]
      if (nextCandidate) {
        this._smart.s = nextCandidate.text || ""
      }
      this._step = 3
      this._render()
    } else {
      // Done - close and trigger refresh
      this._close()
      this._dispatchSaved()
    }
  }

  _dispatchSaved() {
    // Dispatch event so the editor knows to re-render
    this.element.dispatchEvent(new CustomEvent("goal-walkthrough:saved", {
      bubbles: true,
      detail: { goalCount: this._candidates.length }
    }))
  }

  _close() {
    if (this._overlay) {
      this._overlay.remove()
      this._overlay = null
    }
    if (this._escHandler) {
      document.removeEventListener("keydown", this._escHandler)
      this._escHandler = null
    }
  }

  _focusFirst() {
    if (!this._overlay) return
    const first = this._overlay.querySelector(
      "button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled])"
    )
    if (first) {
      setTimeout(() => first.focus(), 0)
    }
  }
}
